import { useRef, useState, useEffect } from "react";
import { useReportStore, ReportData, ConversationFeedback, CriticalMoment } from "../../lib/store-report";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./sales-report.scss";
import { PDF_SCALE } from "../../lib/constants";

const ScoreRing = ({ score }: { score: number }) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        let frame: number;
        const t0 = performance.now();
        const tick = (now: number) => {
            const p = Math.min((now - t0) / 900, 1);
            setVal(Math.round((1 - Math.pow(1 - p, 3)) * score));
            if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [score]);

    const color = score >= 80 ? "#16a34a" : score >= 60 ? "#ca8a04" : score >= 40 ? "#ea580c" : "#dc2626";
    const c = 2 * Math.PI * 46;

    return (
        <div className="sr-score">
            <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="46" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                <circle cx="50" cy="50" r="46" fill="none" stroke={color} strokeWidth="8"
                    strokeDasharray={c} strokeDashoffset={c - (val / 100) * c}
                    strokeLinecap="round" transform="rotate(-90 50 50)" />
            </svg>
            <span className="sr-score-val" style={{ color }}>{val}</span>
        </div>
    );
};

export const SalesReport = ({ onClose }: { onClose: () => void }) => {
    const { isLoading, report, error } = useReportStore();
    const ref = useRef<HTMLDivElement>(null);
    const [pdfBusy, setPdfBusy] = useState(false);

    const downloadPdf = async () => {
        if (!ref.current || pdfBusy) return;
        setPdfBusy(true);
        try {
            await new Promise(r => setTimeout(r, 50));
            const canvas = await html2canvas(ref.current, { scale: PDF_SCALE, useCORS: true, logging: false });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const w = 210, h = (canvas.height * w) / canvas.width;
            let left = h, pos = 0;
            pdf.addImage(imgData, "PNG", 0, pos, w, h);
            left -= 297;
            while (left > 0) { pos = left - h; pdf.addPage(); pdf.addImage(imgData, "PNG", 0, pos, w, h); left -= 297; }
            pdf.save(`satis-raporu-${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (e) { console.error(e); } finally { setPdfBusy(false); }
    };

    if (isLoading || (!report && !error)) {
        return (
            <div className="sr-loading">
                <button className="sr-close-float" onClick={onClose}><span className="material-symbols-outlined">close</span></button>
                <div className="sr-spinner" />
                <p>Rapor hazirlanıyor...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="sr-loading">
                <h2>Rapor oluşturulamadı</h2>
                <p className="sr-err-msg">{error}</p>
                <button className="sr-btn" onClick={onClose}>Geri Dön</button>
            </div>
        );
    }

    const r = report as ReportData;

    return (
        <div className="sr-shell">
            <div className="sr-scroll">
                <article className="sr-page" ref={ref}>

                    {/* Header */}
                    <header className="sr-header">
                        <div className="sr-brand">
                            <img src="/logo.svg" alt="" />
                            <span className="sr-sep" />
                            <span>Performans Raporu</span>
                        </div>
                        <div className="sr-actions">
                            <button className="sr-btn" onClick={downloadPdf} disabled={pdfBusy}>
                                <span className="material-symbols-outlined">download</span>
                                {pdfBusy ? "Hazırlanıyor..." : "PDF İndir"}
                            </button>
                            <button className="sr-icon-btn" onClick={onClose}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    </header>

                    {/* Score + Summary */}
                    <section className="sr-overview">
                        <ScoreRing score={r.overallScore} />
                        <div className="sr-overview-body">
                            <div className={`sr-outcome-badge ${r.saleOutcome.saleMade ? "positive" : ""}`}>
                                {r.saleOutcome.saleMade ? "Satış Yapıldı" : "Satış Yapılamadı"}
                            </div>
                            <p className="sr-summary">{r.shortAnalysis}</p>
                            <p className="sr-outcome-why">{r.saleOutcome.explanation}</p>
                        </div>
                    </section>

                    {/* Persona */}
                    <section className="sr-card">
                        <h2>Müşteri Uyumu</h2>
                        <p>{r.personaAnalysis}</p>
                    </section>

                    {/* Strengths / Improvements */}
                    <section className="sr-two-col">
                        <div className="sr-col">
                            <h2>Güçlü Yönler</h2>
                            <ul className="sr-list positive">
                                {r.positives.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                        <div className="sr-col">
                            <h2>Gelişim Alanları</h2>
                            <ul className="sr-list negative">
                                {r.negatives.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                    </section>

                    {/* Critical Moments */}
                    {r.criticalMoments.length > 0 && (
                        <section className="sr-card">
                            <h2>Kritik Anlar</h2>
                            <div className="sr-moments">
                                {r.criticalMoments.map((m: CriticalMoment, i: number) => (
                                    <div key={i} className={`sr-moment ${m.type === "opportunity_gained" ? "gained" : "missed"}`}>
                                        <div className="sr-moment-head">
                                            <code>{m.timestamp}</code>
                                            <span className="sr-moment-type">
                                                {m.type === "opportunity_gained" ? "Kazanıldı" : "Kaçırıldı"}
                                            </span>
                                        </div>
                                        <p>{m.description}</p>
                                        <p className="sr-moment-tip">{m.suggestion}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Conversation */}
                    <section className="sr-card">
                        <h2>Konuşma Dökümü</h2>
                        <div className="sr-transcript">
                            {r.conversationFeedback.map((c: ConversationFeedback, i: number) => (
                                <div key={i} className={`sr-msg ${c.role}`}>
                                    <div className="sr-msg-row">
                                        <code className="sr-msg-time">{c.timestamp}</code>
                                        <span className="sr-msg-role">{c.role === "customer" ? "Müşteri" : "Satıcı"}</span>
                                        <span className="sr-msg-text">{c.text}</span>
                                    </div>
                                    {c.feedback && <div className="sr-feedback">{c.feedback}</div>}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Detailed Analysis */}
                    {r.detailedAnalysis && (
                        <section className="sr-card sr-analysis">
                            <h2>Detaylı Analiz</h2>
                            {r.detailedAnalysis.split('\n').map((p, i) =>
                                p.trim() ? <p key={i}>{p}</p> : null
                            )}
                        </section>
                    )}

                </article>
            </div>
        </div>
    );
};
